import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import { referralService } from '../../services/ReferralsServices';

const ReferralDetails = () => {
  const { id } = useParams();
  const [referralData, setReferralData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchReferralDetails();
  }, [id]);

  const fetchReferralDetails = async () => {
    try {
      setLoading(true);
      const response = await referralService.getReferralDetailsByUserId(id);
      
      if (response?.data?.success) {
        setReferralData(response.data.data);
      } else {
        setError('Failed to load referral details.');
      }
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch referral details:', error);
      setError('Failed to load referral details.');
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      active: 'bg-success',
      inactive: 'bg-secondary',
      pending: 'bg-warning'
    };
    return (
      <span className={`badge ${statusClasses[status] || 'bg-secondary'}`}>
        {status?.charAt(0).toUpperCase() + status?.slice(1) || 'N/A'}
      </span>
    );
  };

  // Columns for referred users table
  const referredUsersColumns = [
    {
      name: 'Name',
      selector: row => row.fullName || 'N/A',
      sortable: true,
    },
    {
      name: 'Email',
      selector: row => row.email || 'N/A',
      sortable: true,
    },
    {
      name: 'Phone',
      selector: row => row.phoneNumber || 'N/A',
      sortable: true,
    },
    {
      name: 'Join Date',
      selector: row => formatDate(row.createdAt),
      sortable: true,
    },
    {
      name: 'KYC Status',
      cell: row => getStatusBadge(row.kyc_status),
      sortable: true,
    }
  ];

  // Columns for referral earnings table
  const earningsColumns = [
    {
      name: 'Referred User',
      selector: row => row.referred_user_id?.fullName || 'N/A',
      sortable: true,
    },
    {
      name: 'Email',
      selector: row => row.referred_user_id?.email || 'N/A',
      sortable: true,
    },
    {
      name: 'Investment Amount',
      selector: row => formatCurrency(row.investment_amount),
      sortable: true,
      right: true,
    },
    {
      name: 'Earning Amount',
      selector: row => formatCurrency(row.earning_amount),
      sortable: true,
      right: true,
    },
    {
      name: 'Multiplier',
      selector: row => `${row.multiplier_applied}%`,
      sortable: true,
      center: true,
    },
    {
      name: 'Date',
      selector: row => formatDate(row.earning_date),
      sortable: true,
    }
  ];

  // Columns for milestones table
  const milestonesColumns = [
    {
      name: 'Milestone #',
      selector: row => row.milestone_number,
      sortable: true,
      center: true,
    },
    {
      name: 'Referrals Required',
      selector: row => row.referrals_required,
      sortable: true,
      center: true,
    },
    {
      name: 'Multiplier Added',
      selector: row => `${row.multiplier_added}%`,
      sortable: true,
      center: true,
    },
    {
      name: 'Achieved Date',
      selector: row => formatDate(row.achieved_date),
      sortable: true,
    }
  ];

  if (loading) {
    return (
      <div className="body d-flex py-3">
        <div className="container-xxl">
          <div className="d-flex justify-content-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="body d-flex py-3">
        <div className="container-xxl">
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        </div>
      </div>
    );
  }

  if (!referralData) {
    return (
      <div className="body d-flex py-3">
        <div className="container-xxl">
          <div className="alert alert-info" role="alert">
            No referral data found for this user.
          </div>
        </div>
      </div>
    );
  }

  const { referralProgram, referredUsers, stats } = referralData;

  return (
    <div className="body d-flex py-3">
      <div className="container-xxl">
        <div className="row align-items-center mb-4">
          <div className="col">
            <div className="d-flex align-items-center">
              <h3 className="fw-bold mb-0">Referral Details</h3>
            </div>
          </div>
        </div>

        {/* User Info Card */}
        <div className="row mb-4">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header">
                <h5 className="card-title mb-0">User Information</h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6">
                    <p><strong>Name:</strong> {referralProgram.user_id?.fullName || 'N/A'}</p>
                    <p><strong>Email:</strong> {referralProgram.user_id?.email || 'N/A'}</p>
                    <p><strong>Phone:</strong> {referralProgram.user_id?.phoneNumber || 'N/A'}</p>
                  </div>
                  <div className="col-md-6">
                    <p><strong>Referral Code:</strong> 
                      <span className="badge bg-primary ms-2">{referralProgram.referral_code}</span>
                    </p>
                    <p><strong>KYC Status:</strong> {getStatusBadge(referralProgram.user_id?.kyc_status)}</p>
                    <p><strong>Program Status:</strong> {getStatusBadge(referralProgram.status)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="row mb-4">
          <div className="col-md-3">
            <div className="card bg-primary text-white">
              <div className="card-body text-center">
                <h4 className="card-title">{stats.totalReferredUsers}</h4>
                <p className="card-text">Total Referrals</p>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-success text-white">
              <div className="card-body text-center">
                <h4 className="card-title">{stats.activeReferrals}</h4>
                <p className="card-text">Active Referrals</p>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-info text-white">
              <div className="card-body text-center">
                <h4 className="card-title">{formatCurrency(stats.totalEarnings)}</h4>
                <p className="card-text">Total Earnings</p>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-warning text-white">
              <div className="card-body text-center">
                <h4 className="card-title">{stats.currentMultiplier}%</h4>
                <p className="card-text">Current Multiplier</p>
              </div>
            </div>
          </div>
        </div>

        {/* Next Milestone */}
        <div className="row mb-4">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header">
                <h5 className="card-title mb-0">Next Milestone</h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-3">
                    <p><strong>Milestone #:</strong> {referralProgram.next_milestone?.milestone_number || 'N/A'}</p>
                  </div>
                  <div className="col-md-3">
                    <p><strong>Target Referrals:</strong> {referralProgram.next_milestone?.target_referrals || 'N/A'}</p>
                  </div>
                  <div className="col-md-3">
                    <p><strong>Remaining:</strong> {referralProgram.next_milestone?.remaining_referrals || 'N/A'}</p>
                  </div>
                  <div className="col-md-3">
                    <p><strong>Multiplier Reward:</strong> {referralProgram.next_milestone?.multiplier_reward || 'N/A'}%</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Referred Users Table */}
        <div className="row mb-4">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header">
                <h5 className="card-title mb-0">Referred Users ({referredUsers?.length || 0})</h5>
              </div>
              <div className="card-body">
                <DataTable
                  columns={referredUsersColumns}
                  data={referredUsers || []}
                  pagination
                  highlightOnHover
                  noDataComponent="No referred users found"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Referral Earnings Table */}
        <div className="row mb-4">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header">
                <h5 className="card-title mb-0">Referral Earnings ({referralProgram.referral_earnings?.length || 0})</h5>
              </div>
              <div className="card-body">
                <DataTable
                  columns={earningsColumns}
                  data={referralProgram.referral_earnings || []}
                  pagination
                  highlightOnHover
                  noDataComponent="No earnings found"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Milestones Achieved Table */}
        <div className="row mb-4">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header">
                <h5 className="card-title mb-0">Milestones Achieved ({referralProgram.milestones_achieved?.length || 0})</h5>
              </div>
              <div className="card-body">
                <DataTable
                  columns={milestonesColumns}
                  data={referralProgram.milestones_achieved || []}
                  pagination
                  highlightOnHover
                  noDataComponent="No milestones achieved yet"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferralDetails;